import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { Scene } from "@babylonjs/core/scene";
import { resolveShipStats, type ResolvedShipStats, type ShipSpec } from "../config/ships";
import { buildShipModel } from "./shipModel";
import { Boosters } from "./boosters";
import type { ControlState } from "../input/types";
import { crossNormal, crossOffset, type RoadHit, type Track } from "../track/Track";

const HOVER_HEIGHT = 1.2;
const GRAVITY = 80;
const DRIFT_MIN_SPEED = 12;
const BOOST_PAD_SPEED = 364; // target speed while a boost is active
const BOOST_DURATION = 1.4;
const JUMP_VELOCITY = 42; // base launch speed; ramps scale this by their power
const DRIFT_REWARD_TIME = 1.1; // seconds of drift to earn a mini-boost
const WALL_RESTITUTION = 0.3;
/** How quickly raw steer input eases toward the target (per second). Lower =
 * softer, more progressive turn-in; this is what stops side-to-side snapping. */
const STEER_EASE = 8;
/** How quickly the drift state blends in/out (grip, rotation, slip). */
const DRIFT_EASE = 6;
/** Seconds of held drift to ramp from 0% up to the max drift turn. */
const DRIFT_RAMP_TIME = 2.0;
/** Drift turn sharpness at the very start of a drift (fraction of max). */
const DRIFT_SHARP_MIN = 0;
/** Seconds to bleed off drift sharpness after releasing, so each drift earns it. */
const DRIFT_SHARP_DECAY = 0.4;
/** Velocity-realign rate (rad/s) while fully sliding — near zero so momentum is
 * held like a trolley (vs the ship's `grip` rate when traction is engaged). */
const GRIP_SLIDE = 0.2;
/** How far below the road surface counts as "fallen off" → respawn. */
const FALL_LIMIT = 16;
/** Seconds after a respawn that pads stay inert, so dropping back in on a ramp
 * doesn't fire you straight off it again. */
const RESPAWN_PAD_LOCKOUT = 1.0;
/** How fast the hull's "up" follows the road (grounded) or levels out (air). */
const UP_EASE_GROUND = 30;
const UP_EASE_AIR = 8;

const WORLD_UP = new Vector3(0, 1, 0);

/** Rotate `v` about the unit axis `k` by `a` radians. With `k` pointing up, a
 * positive angle turns right — the same sense as steering and yaw. */
function rotateAbout(v: Vector3, k: Vector3, a: number): Vector3 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return v
    .scale(c)
    .addInPlace(Vector3.Cross(k, v).scaleInPlace(s))
    .addInPlace(k.scale(Vector3.Dot(k, v) * (1 - c)));
}

/** Signed angle from `a` to `b` about the axis `k` (+ = `b` is to the right). */
function signedAngle(a: Vector3, b: Vector3, k: Vector3): number {
  return Math.atan2(Vector3.Dot(Vector3.Cross(a, b), k), Vector3.Dot(a, b));
}

/** `v` with its component along the unit `n` removed, rescaled to `len` (if it
 * has any length left). */
function flattenTo(v: Vector3, n: Vector3, len: number): void {
  v.subtractInPlace(n.scale(Vector3.Dot(v, n)));
  const l = v.length();
  if (l > 1e-9) v.scaleInPlace(len / l);
}

/**
 * A hover craft. It rides the road's own surface: "up" is the road's normal
 * wherever the road faces — banked, climbing a wall, round a loop, upside
 * down — and all the handling (steering, traction, drift, thrust) runs in that
 * surface's plane, exactly as it would on flat ground. Only jumps leave it: in
 * the air it flies under world gravity and lands back on the road.
 */
export class Ship {
  readonly root: TransformNode;
  readonly stats: ResolvedShipStats;
  readonly spec: ShipSpec;

  // --- kinematic state ---
  readonly position = new Vector3();
  /** Position at the start of this frame's update — pads test the path swept
   * between the two, so a fast craft can't step over one between frames. */
  readonly prevPosition = new Vector3();
  /** Where the nose points: along the road surface on the ground, level in the air. */
  readonly heading = new Vector3(0, 0, 1);
  /** The surface normal the craft rides on (world up while airborne). */
  readonly up = new Vector3(0, 1, 0);
  /** Velocity: along the road surface on the ground; horizontal (XZ) in the air,
   * with the vertical part in `verticalVel`. */
  readonly velocity = new Vector3();
  verticalVel = 0;
  airborne = false;
  /** The road under the craft as of the last update, and its sample index —
   * the search hint that keeps the craft on its own pass of the loop. */
  road: RoadHit | null = null;
  roadHint = -1;

  // --- feel state (read by HUD / effects) ---
  speed = 0;
  drifting = false;
  driftDir = 0;
  driftCharge = 0;
  boostTimer = 0;
  /** Flashes briefly after a respawn so the HUD/effects can react. */
  respawnFlash = 0;
  /** Pads ignore this craft while > 0 (set on respawn). */
  padLockout = 0;
  /** Per-pad re-trigger cooldowns for this craft (driven by Game), so two
   * racers crossing the same pad both get it. */
  readonly padCooldowns = new Map<object, number>();
  /** Eased steer input (-1..1) — the actual value the physics steers with. */
  private steerInput = 0;
  /** Eased 0..1 drift blend, so traction/rotation/slip transition smoothly.
   * Public so the camera can lean with it smoothly instead of snapping. */
  driftAmount = 0;
  /** 0..1 skill ramp — climbs while a drift is held, so longer drifts turn
   * sharper (up to the ship's max drift turn). */
  private driftSharp = 0;
  /** Visual lean (roll into the turn), smoothed. */
  private bank = 0;
  /** Visual pitch in the air (nose follows the trajectory), smoothed. */
  private pitch = 0;
  private pitchTarget = 0;
  /** The hull's up, eased toward the road's (or world up in the air). */
  private readonly modelUp = new Vector3(0, 1, 0);
  private readonly qBase = new Quaternion();
  private readonly qLocal = new Quaternion();
  private readonly mTmp = new Matrix();

  // Last spot the ship was safely on the road — where a fall respawns it.
  private readonly lastSafe = new Vector3();
  private readonly lastSafeForward = new Vector3(0, 0, 1);
  private readonly lastSafeUp = new Vector3(0, 1, 0);
  private lastSafeHint = -1;

  // --- progress ---
  lap = 0;
  private lastT = 0;
  /** Normalized progress around the loop (0..1) at the last sample. */
  trackT = 0;
  bestLapMs: number | null = null;
  currentLapMs = 0;
  /** Time of the most recently completed lap (ms), or null. */
  lastLapMs: number | null = null;

  /** Total race progress (lap + position around the loop) for ranking racers. */
  get progress(): number {
    return this.lap + this.trackT;
  }

  /** Heading as a yaw angle (rad, 0 = +Z, + = right) — for the HUD, ghosts and
   * anything that only needs the plan-view direction. */
  get yaw(): number {
    return Math.atan2(this.heading.x, this.heading.z);
  }

  constructor(scene: Scene, spec: ShipSpec) {
    this.spec = spec;
    this.stats = resolveShipStats(spec);
    this.root = this.buildModel(scene, spec);
    this.root.rotationQuaternion = new Quaternion();
  }

  private buildModel(scene: Scene, spec: ShipSpec): TransformNode {
    // Preferred: the shared GLB craft, hue-rotated for this ship. Falls back to
    // the simple box hull if the model isn't loaded yet.
    const model = buildShipModel(scene, spec.model, 4.6);
    if (model) {
      const mroot = new TransformNode(`ship-${spec.id}`, scene);
      model.parent = mroot; // nose along +Z, the travel direction
      this.boosters = new Boosters(scene, model, spec.model);
      // The boosters are its engine glow; give the boost-glow hook a no-op target.
      this.glowMat = { emissiveColor: new Color3() };
      return mroot;
    }

    const root = new TransformNode(`ship-${spec.id}`, scene);

    const body = MeshBuilder.CreateBox("hull", { width: 2.2, height: 0.7, depth: 4.2 }, scene);
    const nose = MeshBuilder.CreateCylinder(
      "nose",
      { diameterTop: 0, diameterBottom: 1.6, height: 2.4, tessellation: 4 },
      scene
    );
    nose.rotation.x = Math.PI / 2;
    nose.position.z = 3;
    nose.position.y = 0;
    const fin = MeshBuilder.CreateBox("fin", { width: 0.2, height: 1.0, depth: 1.6 }, scene);
    fin.position.set(0, 0.7, -1.6);

    const hullMat = new StandardMaterial("hullMat", scene);
    hullMat.diffuseColor = spec.color.scale(0.6);
    hullMat.emissiveColor = spec.color.scale(0.35);
    hullMat.specularColor = new Color3(1, 1, 1);
    body.material = hullMat;
    nose.material = hullMat;
    fin.material = hullMat;

    // Engine glow plate at the back, brightens with boost via emissive scaling.
    const glow = MeshBuilder.CreateBox("glow", { width: 1.8, height: 0.4, depth: 0.3 }, scene);
    glow.position.set(0, 0, -2.2);
    const glowMat = new StandardMaterial("glowMat", scene);
    glowMat.emissiveColor = new Color3(0.4, 0.9, 1);
    glow.material = glowMat;
    this.glowMat = glowMat;

    for (const m of [body, nose, fin, glow]) m.parent = root;
    return root;
  }

  private glowMat!: { emissiveColor: Color3 };
  /** Engine flames (craft models only; the box fallback has its glow plate). */
  private boosters: Boosters | null = null;

  placeAtStart(position: Vector3, forward: Vector3): void {
    this.position.copyFrom(position);
    this.position.y += HOVER_HEIGHT;
    this.heading.copyFrom(forward).normalize();
    this.up.copyFrom(WORLD_UP);
    this.modelUp.copyFrom(WORLD_UP);
    this.velocity.setAll(0);
    this.verticalVel = 0;
    this.airborne = false;
    this.road = null;
    this.roadHint = -1;
    this.speed = 0;
    this.steerInput = 0;
    this.driftAmount = 0;
    this.driftSharp = 0;
    this.bank = 0;
    this.pitch = 0;
    this.pitchTarget = 0;
    this.lap = 0;
    this.currentLapMs = 0;
    this.lastT = 0;
    this.lastSafe.copyFrom(position);
    this.lastSafeForward.copyFrom(this.heading);
    this.lastSafeUp.copyFrom(WORLD_UP);
    this.lastSafeHint = -1;
    this.prevPosition.copyFrom(this.position);
    this.padLockout = 0;
    this.padCooldowns.clear();
    this.syncTransform();
  }

  /** Online: put this craft where another device says it is. Its physics doesn't run
   * here; only its pose, its speed (for the flames) and the flames themselves. */
  showAt(position: Vector3, rotation: Quaternion, speed: number, boosting: boolean, dt: number): void {
    this.position.copyFrom(position);
    this.speed = speed;
    this.root.position.copyFrom(position);
    this.root.rotationQuaternion!.copyFrom(rotation);
    this.boosters?.update(dt, speed / this.stats.maxSpeed, boosting);
  }

  // ---- physics --------------------------------------------------------------

  update(dt: number, ctrl: ControlState, track: Track): void {
    this.prevPosition.copyFrom(this.position);
    if (this.padLockout > 0) this.padLockout -= dt;

    // Ease the raw steer toward its target. Digital inputs (keyboard, d-pad)
    // snap to ±1; easing them in/out is what stops the ship flicking between
    // the edges of the screen and makes turn-in (and drifts) feel progressive.
    this.steerInput += (ctrl.steer - this.steerInput) * Math.min(1, STEER_EASE * dt);
    const steer = this.steerInput;

    const braking = ctrl.brake > 0.1;
    let speed = this.velocity.length();

    // Air-brake = release traction. Holding it lets the ship keep sliding on its
    // current momentum (like a shopping trolley) while you rotate the body with
    // steering; releasing re-engages grip, which swings the velocity round to
    // face where the ship now points and carries you off that way. It's purely a
    // traction toggle — no braking, no speed loss.
    const sliding = braking && speed > DRIFT_MIN_SPEED;
    this.drifting = sliding;
    if (sliding && Math.abs(steer) > 0.05) this.driftDir = Math.sign(steer);
    // Release traction smoothly, but re-engage grip FAST so letting go snaps the
    // velocity round to the facing direction (the "catch" that fires you off).
    const driftEase = sliding ? DRIFT_EASE : DRIFT_EASE * 3;
    this.driftAmount += ((sliding ? 1 : 0) - this.driftAmount) * Math.min(1, driftEase * dt);

    // Skill ramp: the longer the slide is held, the harder you can rotate it
    // (0 -> max over DRIFT_RAMP_TIME), bleeding off after release.
    if (sliding) this.driftSharp = Math.min(1, this.driftSharp + dt / DRIFT_RAMP_TIME);
    else this.driftSharp = Math.max(0, this.driftSharp - dt / DRIFT_SHARP_DECAY);
    const sharp = DRIFT_SHARP_MIN + (1 - DRIFT_SHARP_MIN) * this.driftSharp;

    // The plane the handling runs in: the road surface, or level in the air.
    const axis = this.airborne ? WORLD_UP : this.up;

    // --- steering rotates the heading (the body), independent of momentum ---
    const speedFactor = Math.min(1, 0.4 + speed / this.stats.maxSpeed);
    const turnMul = 1 + (this.stats.driftTurnMultiplier - 1) * this.driftAmount * sharp;
    this.heading.copyFrom(rotateAbout(this.heading, axis, steer * this.stats.turnRate * speedFactor * turnMul * dt));

    // --- traction: rotate the velocity DIRECTION toward the heading, preserving
    // speed. Full grip normally (you go where you point); eased to ~frozen while
    // sliding (momentum held). On release this swing is the "catch" that fires
    // you off in the facing direction. ---
    if (speed > 0.5) {
      const dYaw = signedAngle(this.velocity, this.heading, axis);
      const gripRate = this.stats.grip + (GRIP_SLIDE - this.stats.grip) * this.driftAmount;
      const stepAng = Math.max(-gripRate * dt, Math.min(gripRate * dt, dYaw));
      this.velocity.copyFrom(rotateAbout(this.velocity, axis, stepAng));
    } else {
      this.velocity.copyFrom(this.heading.scale(speed));
    }

    // --- speed magnitude: accelerate toward the (boosted) top speed, then hold
    // it. Acceleration (thrust) sets the CLIMB RATE; top speed (maxSpeed) is the
    // CEILING — they're decoupled so both stats are felt distinctly. Drag only
    // bites ABOVE the target, to bleed boost overspeed back down to cruising; it
    // never caps you below maxSpeed (which used to make topSpeed barely matter).
    // Direction is untouched here, so cornering keeps the speed you carry. ---
    speed = this.velocity.length();
    const boosting = this.boostTimer > 0 && !this.airborne;
    const targetSpeed = boosting ? BOOST_PAD_SPEED : this.stats.maxSpeed;
    let thrust = boosting ? this.stats.thrust * 3 : this.stats.thrust;
    if (this.airborne) thrust *= 0.15;
    let newSpeed = speed;
    if (newSpeed < targetSpeed) {
      newSpeed = Math.min(targetSpeed, newSpeed + thrust * dt);
    } else if (newSpeed > targetSpeed) {
      newSpeed = Math.max(targetSpeed, newSpeed - newSpeed * this.stats.drag * dt);
    }
    if (speed > 0.001) this.velocity.scaleInPlace(newSpeed / speed);
    else this.velocity.copyFrom(this.heading.scale(newSpeed));

    // --- drift reward: bank time drifting into a short boost on release ---
    if (this.drifting) {
      this.driftCharge += dt;
    } else if (this.driftCharge > 0) {
      if (this.driftCharge >= DRIFT_REWARD_TIME && this.boostTimer <= 0) {
        this.boostTimer = 0.6;
      }
      this.driftCharge = 0;
    }
    if (this.boostTimer > 0) this.boostTimer -= dt;

    // --- move ---
    this.position.addInPlace(this.velocity.scale(dt));
    if (this.airborne) {
      this.verticalVel -= GRAVITY * dt;
      this.position.y += this.verticalVel * dt;
    }

    const hit = track.project(this.position, this.roadHint);
    this.roadHint = hit.i;
    this.road = hit;

    if (this.airborne) {
      // You only land if you come down *over the road*. Sail past its edge and
      // there's nothing to catch you — that's the gamble on a shortcut jump.
      const towardSurface = Vector3.Dot(this.velocity, hit.up) + this.verticalVel * hit.up.y;
      if (towardSurface <= 0 && Math.abs(hit.u) <= hit.halfWidth + 1 && hit.h <= HOVER_HEIGHT) {
        this.land(hit);
      } else if (this.belowRoad(hit) < -FALL_LIMIT) {
        // Fell off the course (missed a jump / launched off the side): respawn.
        this.respawn();
        return;
      } else {
        // Nose follows the trajectory: up while rising, down while falling.
        const hSpeed = Math.max(8, Math.hypot(this.velocity.x, this.velocity.z));
        this.pitchTarget = -Math.atan2(this.verticalVel, hSpeed);
      }
    } else {
      this.ride(hit);
    }

    if (!this.airborne) {
      // --- soft walls: only bite when grounded, so jumps can fly off-track.
      // A closed pipe or cylinder has no edges: you just carry on round. ---
      const limit = hit.halfWidth - 1.5;
      if (!hit.closed && Math.abs(hit.u) > limit) {
        const side = Math.sign(hit.u);
        const over = Math.abs(hit.u) - limit;
        this.position.addInPlace(hit.right.scale(-side * over));
        // bounce the velocity across the road back inward, scrub speed
        const intoWall = Vector3.Dot(this.velocity, hit.right) * side;
        if (intoWall > 0) {
          this.velocity.addInPlace(hit.right.scale(-side * intoWall * (1 + WALL_RESTITUTION)));
          this.velocity.scaleInPlace(0.9);
        }
      }
      // On the road → this is a safe place to respawn back to (its centre
      // line: the floor of a pipe, the top of a cylinder).
      this.lastSafe.copyFrom(hit.center);
      this.lastSafeForward.copyFrom(hit.forward);
      this.lastSafeUp.copyFrom(hit.spineUp);
      this.lastSafeHint = hit.i;
      this.pitchTarget = 0;
    }

    if (this.respawnFlash > 0) this.respawnFlash -= dt;
    this.speed = this.velocity.length();
    this.updateProgress(hit, dt);
    this.updateVisuals(dt);
    this.syncTransform();
    // After the craft has moved, so the sparks ride this frame's nozzles.
    this.boosters?.update(dt, this.speed / this.stats.maxSpeed, this.boostTimer > 0);
  }

  /** Grounded: sit on the surface under the craft and carry the velocity and
   * heading round onto it (its plane turns as the road climbs, banks or loops). */
  private ride(hit: RoadHit): void {
    // Only the height above the surface is corrected; where the craft is along
    // and across the road is left exactly as it moved.
    this.position.addInPlace(hit.up.scale(HOVER_HEIGHT - hit.h));
    flattenTo(this.velocity, hit.up, this.velocity.length());
    flattenTo(this.heading, hit.up, 1);
    if (this.heading.lengthSquared() < 0.5) this.heading.copyFrom(hit.forward);
    this.up.copyFrom(hit.up);
  }

  /** How far above (+) or below (-) the road the craft is, for the fall test.
   * Past the road's edge the surface doesn't carry on: it's measured from the
   * edge. On an upright road that's straight up and down (a craft flying out
   * beside a banked section isn't "under" the tilt); on a wall or in a loop,
   * along the road's up. */
  private belowRoad(hit: RoadHit): number {
    const across = Math.max(-hit.halfWidth, Math.min(hit.halfWidth, hit.u));
    if (hit.kappa !== 0) {
      // A curled road: the same, measured from its curve.
      const at = hit.center.add(crossOffset(hit.spineUp, hit.spineRight, hit.kappa, across));
      const n = crossNormal(hit.spineUp, hit.spineRight, hit.kappa, across);
      if (n.y > 0.3) return this.position.y - at.y;
      return Vector3.Dot(this.position.subtract(at), n);
    }
    const edge = hit.center.add(hit.right.scale(across));
    if (hit.up.y > 0.3) return this.position.y - edge.y;
    return Vector3.Dot(this.position.subtract(edge), hit.up);
  }

  /** Touch down: onto the surface, keeping the speed you flew at. */
  private land(hit: RoadHit): void {
    const hSpeed = this.velocity.length();
    this.velocity.y += this.verticalVel;
    this.verticalVel = 0;
    this.airborne = false;
    this.ride(hit);
    flattenTo(this.velocity, hit.up, hSpeed);
  }

  private updateProgress(sample: { t: number }, dt: number): void {
    this.trackT = sample.t;
    this.currentLapMs += dt * 1000;
    // crossing the start line (t wraps from ~1 back to ~0) counts a lap
    if (this.lastT > 0.8 && sample.t < 0.2) {
      if (this.lap > 0) {
        this.lastLapMs = this.currentLapMs;
        if (this.bestLapMs == null || this.currentLapMs < this.bestLapMs) {
          this.bestLapMs = this.currentLapMs;
        }
      }
      this.lap++;
      this.currentLapMs = 0;
    }
    this.lastT = sample.t;
  }

  private updateVisuals(dt: number): void {
    // Lean into turns (exaggerated as the drift blends in); the road's own bank
    // comes from riding its surface. Eased so it blends instead of snapping.
    const targetBank = -this.steerInput * (0.3 + 0.25 * this.driftAmount);
    this.bank += (targetBank - this.bank) * Math.min(1, 8 * dt);
    // Nose pitch: level with the road on the ground, the trajectory in the air.
    this.pitch += (this.pitchTarget - this.pitch) * Math.min(1, 8 * dt);
    // The hull's up follows the road's (almost at once), or levels out in the air.
    const target = this.airborne ? WORLD_UP : this.up;
    const k = Math.min(1, (this.airborne ? UP_EASE_AIR : UP_EASE_GROUND) * dt);
    this.modelUp.addInPlace(target.subtract(this.modelUp).scaleInPlace(k)).normalize();
    // Engine glow pulses with boost / speed.
    const intensity = this.boostTimer > 0 ? 2.2 : 0.6 + (this.speed / this.stats.maxSpeed) * 0.8;
    this.glowMat.emissiveColor.set(0.4 * intensity, 0.9 * intensity, 1.0 * intensity);
  }

  private syncTransform(): void {
    this.root.position.copyFrom(this.position);
    // The hull's frame: nose along the heading, laid on its up...
    const up = this.modelUp;
    const f = this.heading.subtract(up.scale(Vector3.Dot(this.heading, up)));
    if (f.lengthSquared() < 1e-9) f.copyFrom(this.heading);
    f.normalize();
    const right = Vector3.Cross(up, f).normalize();
    const upOrtho = Vector3.Cross(f, right);
    Matrix.FromXYZAxesToRef(right, upOrtho, f, this.mTmp);
    Quaternion.FromRotationMatrixToRef(this.mTmp, this.qBase);
    // ...then the pitch and lean on top, in its own frame.
    Quaternion.RotationYawPitchRollToRef(0, this.pitch, this.bank, this.qLocal);
    this.qBase.multiplyToRef(this.qLocal, this.root.rotationQuaternion!);
  }

  // ---- pad / power-up hooks (called by Game) --------------------------------

  /** Boost from a track pad. An angled pad (`dir`) also slings your momentum and
   * heading that way, so a boost can fire you into a corner, not just forward. */
  applyBoostPad(dir?: Vector3): void {
    // A boost strip is on the road surface: flying over one does nothing.
    if (this.airborne) return;
    this.boostTimer = BOOST_DURATION;
    if (dir) {
      const d = dir.subtract(this.up.scale(Vector3.Dot(dir, this.up)));
      if (d.lengthSquared() < 1e-9) return;
      d.normalize();
      this.velocity.copyFrom(d.scale(this.velocity.length()));
      this.heading.copyFrom(d);
    }
  }

  /**
   * Launch off a ramp/jump pad. `power` scales the vertical kick (and thus the
   * hang time and distance covered). We also top up forward momentum so a
   * shortcut jump actually carries the ship across a gap — but the player still
   * has to bring enough speed in, and steers in the air to nail the landing.
   * An angled pad (`dir`) flings you off-axis: the launch follows the pad's
   * heading and the nose turns to match, so diagonal gap-jumps work.
   */
  applyJumpPad(power = 1, dir?: Vector3): void {
    if (this.airborne) return;
    this.airborne = true;
    this.verticalVel = JUMP_VELOCITY * power;
    // In the air the craft flies level: carry the surface speed across flat.
    flattenTo(this.velocity, WORLD_UP, this.velocity.length());
    flattenTo(this.heading, WORLD_UP, 1);
    if (this.heading.lengthSquared() < 0.5) this.heading.copyFrom(this.velocity).normalize();

    const forward = dir ? new Vector3(dir.x, 0, dir.z).normalize() : this.heading.clone();
    if (dir) this.heading.copyFrom(forward);
    const vF = Vector3.Dot(this.velocity, forward);
    const target = Math.max(vF, this.stats.maxSpeed * 0.9);
    this.velocity.addInPlace(forward.scale(target - vF));
  }

  private respawn(): void {
    this.position.copyFrom(this.lastSafe).addInPlace(this.lastSafeUp.scale(HOVER_HEIGHT));
    this.airborne = false;
    this.verticalVel = 0;
    this.heading.copyFrom(this.lastSafeForward);
    this.up.copyFrom(this.lastSafeUp);
    this.modelUp.copyFrom(this.lastSafeUp);
    this.roadHint = this.lastSafeHint;
    // Drop back in slow — losing your speed is the cost of missing the gap.
    this.velocity.copyFrom(this.lastSafeForward.scale(this.stats.maxSpeed * 0.2));
    this.speed = this.velocity.length();
    this.drifting = false;
    this.driftCharge = 0;
    this.boostTimer = 0;
    this.pitch = 0;
    this.pitchTarget = 0;
    this.respawnFlash = 1;
    // The drop-in point is usually the lip you launched from: don't let its
    // ramp fire again underneath you (nor sweep a pad across the teleport).
    this.padLockout = RESPAWN_PAD_LOCKOUT;
    this.prevPosition.copyFrom(this.position);
    this.syncTransform();
  }

  get speedKph(): number {
    // Cosmetic scaling for the HUD: maps cruising/boost into a believable
    // arcade range (~0..1000, boost spiking toward the top).
    return Math.round(this.speed * 5);
  }

  get speedRatio(): number {
    return Math.min(1.2, this.speed / this.stats.maxSpeed);
  }

  /** 0..1 boost gauge for the HUD: full while a boost is firing, otherwise the
   * drift-charge building toward the next reward boost. */
  get boostMeter(): number {
    if (this.boostTimer > 0) return 1;
    return Math.min(1, this.driftCharge / DRIFT_REWARD_TIME);
  }
}
